import { buildSdTestAppFetchBaseUrl } from "./sdTestAppHttpClient";

export type SdTestAppControlLevel = "local" | "remote";

export interface SdTestAppCapabilitiesSnapshot {
  benchMachineId?: string | null;
  benchStationId?: string | null;
  engineVersion?: string | null;
  apiBaseUrl?: string | null;
}

export interface ResolveSdTestAppControlLevelResult {
  controlLevel: SdTestAppControlLevel;
  loopbackReachable: boolean;
  loopbackMachineId: string | null;
  targetMachineId: string | null;
  message: string | null;
}

function normalizeMachineId(value: string | null | undefined): string | null {
  const trimmed = String(value ?? "").trim();
  return trimmed === "" ? null : trimmed;
}

export function machineIdFromCapabilities(capabilities: SdTestAppCapabilitiesSnapshot): string | null {
  return normalizeMachineId(capabilities.benchMachineId ?? capabilities.benchStationId);
}

/**
 * Decide whether the browser may actuate the target bench via loopback or must use Hub relay.
 * Loopback health alone is insufficient when another SDTestApp instance is running locally.
 */
export async function resolveSdTestAppControlLevel(
  targetMachineId: string | null | undefined,
  port: string,
): Promise<ResolveSdTestAppControlLevelResult> {
  const normalizedTarget = normalizeMachineId(targetMachineId);
  const baseUrl = buildSdTestAppFetchBaseUrl(port);

  let loopbackReachable = false;
  let loopbackMachineId: string | null = null;

  try {
    const healthResponse = await fetch(`${baseUrl}/api/hub/v1/health`, { method: "GET" });
    if (!healthResponse.ok) {
      return {
        controlLevel: "remote",
        loopbackReachable: false,
        loopbackMachineId: null,
        targetMachineId: normalizedTarget,
        message: normalizedTarget
          ? "No SDTestApp engine on this PC for the selected bench — remote observe only."
          : "SDTestApp is not running on this PC.",
      };
    }
    loopbackReachable = true;

    const capabilitiesResponse = await fetch(`${baseUrl}/api/hub/v1/capabilities`, { method: "GET" });
    if (capabilitiesResponse.ok) {
      const capabilities = (await capabilitiesResponse.json()) as SdTestAppCapabilitiesSnapshot;
      loopbackMachineId = machineIdFromCapabilities(capabilities);
    }
  } catch {
    return {
      controlLevel: "remote",
      loopbackReachable: false,
      loopbackMachineId: null,
      targetMachineId: normalizedTarget,
      message: normalizedTarget
        ? "Could not reach a local SDTestApp engine — remote observe only."
        : "SDTestApp is not running on this PC.",
    };
  }

  if (!normalizedTarget) {
    return {
      controlLevel: "local",
      loopbackReachable,
      loopbackMachineId,
      targetMachineId: null,
      message: null,
    };
  }

  if (loopbackMachineId && loopbackMachineId === normalizedTarget) {
    return {
      controlLevel: "local",
      loopbackReachable,
      loopbackMachineId,
      targetMachineId: normalizedTarget,
      message: null,
    };
  }

  const localOtherMessage = loopbackMachineId
    ? `This PC is connected to SDTestApp "${loopbackMachineId}", not "${normalizedTarget}". Remote observe only for the selected bench.`
    : `Local SDTestApp did not report a bench machine id. Remote observe only for "${normalizedTarget}".`;

  return {
    controlLevel: "remote",
    loopbackReachable,
    loopbackMachineId,
    targetMachineId: normalizedTarget,
    message: localOtherMessage,
  };
}
